// GradeExtractionProcessesPage.js
import React, { useMemo, useState } from "react";

const processTypes = ["APPROVAL_WORKFLOW", "GRADEBOOK_EXTRACT"];

const dateTimeShort = (isoOrText) => {
    if (!isoOrText) return "";
    const d = new Date(isoOrText);
    if (!Number.isNaN(d.getTime())) {
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
        })}`;
    }
    return String(isoOrText);
};

const GradeExtractionProcessesPage = ({
                                          isAdmin,
                                          processes,
                                          onCreate,
                                          onUpdate,
                                          onDelete,
                                          onRun,
                                          onToggleEnabled
                                      }) => {
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    // Edit modal
    const [editItem, setEditItem] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);

    // Add modal
    const [newItem, setNewItem] = useState({
        id: "",
        name: "",
        description: "",
        type: "GRADEBOOK_EXTRACT",
        enabled: true,
        lastEvent: ""
    });
    const [isAddOpen, setIsAddOpen] = useState(false);

    // Configure panel (in-page details form)
    const [configItem, setConfigItem] = useState(null);

    const filtered = useMemo(() => {
        const s = (search || "").trim().toLowerCase();
        if (!s) return processes;
        return processes.filter((p) => {
            return (
                p.name?.toLowerCase().includes(s) ||
                (p.description || "").toLowerCase().includes(s) ||
                (p.type || "").toLowerCase().includes(s) ||
                (p.lastEvent || "").toLowerCase().includes(s)
            );
        });
    }, [processes, search]);

    const sorted = useMemo(() => {
        const dir = sortDir === "desc" ? -1 : 1;
        const k = sortKey;
        return [...filtered].sort((a, b) => {
            const va = a?.[k];
            const vb = b?.[k];

            if (k === "enabled") {
                return (Number(!!va) - Number(!!vb)) * dir;
            }
            if (k === "lastEvent") {
                const da = new Date(va || 0).getTime();
                const db = new Date(vb || 0).getTime();
                return (da - db) * dir;
            }

            const sa = (va ?? "").toString().toLowerCase();
            const sb = (vb ?? "").toString().toLowerCase();
            return sa.localeCompare(sb) * dir;
        });
    }, [filtered, sortKey, sortDir]);

    const onSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    // ---- Edit modal helpers ----
    const openEdit = (proc) => {
        setEditItem({
            ...proc,
            lastEvent: proc.lastEvent || ""
        });
        setIsEditOpen(true);
    };

    const closeEdit = () => {
        setIsEditOpen(false);
        setEditItem(null);
    };

    const handleSaveEdit = async () => {
        if (!editItem) return;
        const payload = {
            id: editItem.id,
            name: (editItem.name || "").trim(),
            description: (editItem.description || "").trim(),
            type: editItem.type || "GRADEBOOK_EXTRACT",
            enabled: !!editItem.enabled,
            lastEvent: editItem.lastEvent || ""
        };
        await onUpdate(payload);
        closeEdit();
    };

    // ---- Add modal helpers ----
    const openAdd = () => {
        setNewItem({
            id: "",
            name: "",
            description: "",
            type: "GRADEBOOK_EXTRACT",
            enabled: true,
            lastEvent: ""
        });
        setIsAddOpen(true);
    };

    const closeAdd = () => setIsAddOpen(false);

    const handleSaveNew = async () => {
        await onCreate(newItem);
        closeAdd();
    };

    // ---- Configure panel helpers ----
    const openConfigure = (proc) => {
        setConfigItem({
            ...proc,
            lastEvent: proc.lastEvent || ""
        });
        // Scroll panel into view in long lists
        setTimeout(() => {
            const el = document.getElementById("grade-process-config-panel");
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 0);
    };

    const closeConfigure = () => {
        setConfigItem(null);
    };

    const handleSaveConfigure = async () => {
        if (!configItem) return;
        const payload = {
            id: configItem.id,
            name: (configItem.name || "").trim(),
            description: (configItem.description || "").trim(),
            type: configItem.type || "GRADEBOOK_EXTRACT",
            enabled: !!configItem.enabled,
            lastEvent: configItem.lastEvent || ""
        };
        await onUpdate(payload);
        closeConfigure();
    };

    const handleDeleteClick = (proc) => {
        if (!window.confirm(`Delete process "${proc.name}"?`)) return;
        onDelete(proc);
    };

    return (
        <>
            {/* Top row: Create + search */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8
                }}
            >
                <div>
                    {isAdmin && (
                        <button type="button" onClick={openAdd}>
                            Create
                        </button>
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                        placeholder="Search extract name, description, type, last event…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ minWidth: 260 }}
                    />
                    <span style={{ fontSize: 12, opacity: 0.7 }}>
                        Displaying {sorted.length} of {processes.length} items
                    </span>
                </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", marginTop: 4 }}>
                <table
                    className="bb-table"
                    style={{ width: "100%", borderCollapse: "collapse" }}
                >
                    <thead>
                    <tr>
                        {[
                            { key: "name", label: "EXTRACT NAME" },
                            { key: "description", label: "DESCRIPTION" },
                            { key: "type", label: "TYPE" },
                            { key: "enabled", label: "ENABLED" },
                            { key: "lastEvent", label: "LAST EVENT" },
                            { key: "__configure", label: "CONFIGURE" },
                            { key: "__edit", label: "EDIT" },
                            { key: "__delete", label: "DELETE" },
                            { key: "__run", label: "RUN" }
                        ].map((h) => {
                            const sortable = !h.key.startsWith("__");
                            const active = h.key === sortKey;
                            const arrow =
                                sortable && active
                                    ? sortDir === "asc"
                                        ? "▲"
                                        : "▼"
                                    : "";
                            return (
                                <th
                                    key={h.key}
                                    onClick={() => sortable && onSort(h.key)}
                                    style={{
                                        borderBottom: "1px solid #ddd",
                                        padding: "8px 6px",
                                        textAlign:
                                            h.key === "enabled" ||
                                            h.key === "__configure" ||
                                            h.key === "__edit" ||
                                            h.key === "__delete" ||
                                            h.key === "__run"
                                                ? "center"
                                                : "left",
                                        cursor: sortable ? "pointer" : "default",
                                        whiteSpace: "nowrap",
                                        fontSize: 12,
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {h.label} {arrow}
                                </th>
                            );
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    {sorted.map((proc) => (
                        <tr key={proc.id}>
                            <td
                                style={{
                                    padding: "8px 6px",
                                    verticalAlign: "top",
                                    fontWeight: 600
                                }}
                            >
                                {proc.name}
                            </td>
                            <td
                                style={{
                                    padding: "8px 6px",
                                    verticalAlign: "top"
                                }}
                            >
                                {proc.description || "—"}
                            </td>
                            <td
                                style={{
                                    padding: "8px 6px",
                                    verticalAlign: "top"
                                }}
                            >
                                {proc.type || "—"}
                            </td>
                            <td
                                style={{
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    verticalAlign: "top"
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={!!proc.enabled}
                                    disabled={!isAdmin}
                                    onChange={() => onToggleEnabled(proc)}
                                />
                            </td>
                            <td
                                style={{
                                    padding: "8px 6px",
                                    verticalAlign: "top",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {proc.lastEvent
                                    ? dateTimeShort(proc.lastEvent)
                                    : "—"}
                            </td>

                            {/* Configure */}
                            <td
                                style={{
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    verticalAlign: "top"
                                }}
                            >
                                {isAdmin ? (
                                    <button
                                        type="button"
                                        onClick={() => openConfigure(proc)}
                                        title="Configure"
                                    >
                                        🔍
                                    </button>
                                ) : (
                                    <span style={{ opacity: 0.4 }}>—</span>
                                )}
                            </td>

                            {/* Edit */}
                            <td
                                style={{
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    verticalAlign: "top"
                                }}
                            >
                                {isAdmin ? (
                                    <button
                                        type="button"
                                        onClick={() => openEdit(proc)}
                                        title="Edit"
                                    >
                                        ✏️
                                    </button>
                                ) : (
                                    <span style={{ opacity: 0.4 }}>—</span>
                                )}
                            </td>

                            {/* Delete */}
                            <td
                                style={{
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    verticalAlign: "top"
                                }}
                            >
                                {isAdmin ? (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteClick(proc)}
                                        title="Delete"
                                    >
                                        🗑
                                    </button>
                                ) : (
                                    <span style={{ opacity: 0.4 }}>—</span>
                                )}
                            </td>

                            {/* Run */}
                            <td
                                style={{
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    verticalAlign: "top"
                                }}
                            >
                                {isAdmin ? (
                                    <button
                                        type="button"
                                        onClick={() => onRun(proc)}
                                        title="Run now"
                                    >
                                        ▶
                                    </button>
                                ) : (
                                    <span style={{ opacity: 0.4 }}>—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    {!sorted.length && (
                        <tr>
                            <td
                                colSpan={9}
                                style={{
                                    padding: 16,
                                    textAlign: "center",
                                    opacity: 0.8
                                }}
                            >
                                No matching grade extraction processes.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Footer paging-style note */}
            <div
                style={{
                    marginTop: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 12
                }}
            >
                <span>
                    Displaying 1 to {sorted.length} of {processes.length} item(s)
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                    <button
                        type="button"
                        onClick={() =>
                            window.alert(
                                "Paging behavior is not implemented in this sample."
                            )
                        }
                    >
                        Show All
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            window.alert(
                                "Paging settings dialog not implemented in this sample."
                            )
                        }
                    >
                        Edit Paging…
                    </button>
                </div>
            </div>

            {/* Configure Panel */}
            {isAdmin && configItem && (
                <ProcessConfigPanel
                    item={configItem}
                    onChange={setConfigItem}
                    onCancel={closeConfigure}
                    onSave={handleSaveConfigure}
                />
            )}

            {/* Edit Modal */}
            {isAdmin && isEditOpen && editItem && (
                <ProcessEditModal
                    item={editItem}
                    onChange={setEditItem}
                    onCancel={closeEdit}
                    onSave={handleSaveEdit}
                />
            )}

            {/* Add Modal */}
            {isAdmin && isAddOpen && (
                <ProcessAddModal
                    item={newItem}
                    onChange={setNewItem}
                    onCancel={closeAdd}
                    onSave={handleSaveNew}
                />
            )}
        </>
    );
};

/* ------------- Configure panel (inline, like video UX) ------------- */

const ProcessConfigPanel = ({ item, onChange, onCancel, onSave }) => (
    <div
        id="grade-process-config-panel"
        style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 4,
            border: "1px solid #ddd",
            background: "#fafafa"
        }}
    >
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8
            }}
        >
            <div style={{ fontWeight: 600 }}>Configure Grade Extraction Process</div>
            <button type="button" onClick={onCancel}>
                Close
            </button>
        </div>

        <div
            style={{
                display: "grid",
                gridTemplateColumns: "220px minmax(200px, 600px)",
                columnGap: 16,
                rowGap: 8,
                fontSize: 13
            }}
        >
            <LabelCell>Id</LabelCell>
            <div>
                <input value={item.id} disabled />
            </div>

            <LabelCell>Extract Name</LabelCell>
            <div>
                <input
                    value={item.name}
                    onChange={(e) => onChange({ ...item, name: e.target.value })}
                />
            </div>

            <LabelCell>Description</LabelCell>
            <div>
                <textarea
                    rows={3}
                    value={item.description || ""}
                    onChange={(e) =>
                        onChange({ ...item, description: e.target.value })
                    }
                />
            </div>

            <LabelCell>Type</LabelCell>
            <div>
                <select
                    value={item.type}
                    onChange={(e) => onChange({ ...item, type: e.target.value })}
                >
                    {processTypes.map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>
            </div>

            <LabelCell>Enabled</LabelCell>
            <div>
                <input
                    type="checkbox"
                    checked={!!item.enabled}
                    onChange={(e) =>
                        onChange({ ...item, enabled: e.target.checked })
                    }
                />
            </div>

            <LabelCell>Last Event</LabelCell>
            <div>
                <input
                    placeholder="Optional; ISO datetime or note"
                    value={item.lastEvent || ""}
                    onChange={(e) =>
                        onChange({ ...item, lastEvent: e.target.value })
                    }
                />
                <div
                    style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}
                >
                    This value is informational and typically set by the job runner
                    when the process executes.
                </div>
            </div>
        </div>

        <div
            style={{
                marginTop: 12,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end"
            }}
        >
            <button type="button" onClick={onCancel}>
                Cancel
            </button>
            <button type="button" onClick={onSave}>
                Save
            </button>
        </div>
    </div>
);

/* ------------- Existing modals (Edit / Add) ------------- */

const ProcessEditModal = ({ item, onChange, onCancel, onSave }) => (
    <div
        role="dialog"
        aria-modal="true"
        style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
        }}
        onClick={onCancel}
    >
        <div
            style={{
                background: "#fff",
                padding: 16,
                borderRadius: 8,
                minWidth: 480,
                maxWidth: "90vw"
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <h3 style={{ marginTop: 0 }}>Edit Grade Extraction Process</h3>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    gap: 8,
                    alignItems: "center"
                }}
            >
                <label>Id</label>
                <input value={item.id} disabled />

                <label>Name</label>
                <input
                    value={item.name}
                    onChange={(e) => onChange({ ...item, name: e.target.value })}
                />

                <label>Description</label>
                <textarea
                    rows={3}
                    value={item.description || ""}
                    onChange={(e) =>
                        onChange({ ...item, description: e.target.value })
                    }
                />

                <label>Type</label>
                <select
                    value={item.type}
                    onChange={(e) => onChange({ ...item, type: e.target.value })}
                >
                    {processTypes.map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>

                <label>Enabled</label>
                <input
                    type="checkbox"
                    checked={!!item.enabled}
                    onChange={(e) =>
                        onChange({ ...item, enabled: e.target.checked })
                    }
                />

                <label>Last Event</label>
                <input
                    placeholder="Optional; ISO datetime or note"
                    value={item.lastEvent || ""}
                    onChange={(e) =>
                        onChange({ ...item, lastEvent: e.target.value })
                    }
                />
            </div>

            <div
                style={{
                    marginTop: 12,
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end"
                }}
            >
                <button type="button" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" onClick={onSave}>
                    Save
                </button>
            </div>
        </div>
    </div>
);

const ProcessAddModal = ({ item, onChange, onCancel, onSave }) => (
    <div
        role="dialog"
        aria-modal="true"
        style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
        }}
        onClick={onCancel}
    >
        <div
            style={{
                background: "#fff",
                padding: 16,
                borderRadius: 8,
                minWidth: 480,
                maxWidth: "90vw"
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <h3 style={{ marginTop: 0 }}>Create Grade Extraction Process</h3>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    gap: 8,
                    alignItems: "center"
                }}
            >
                <label>Id</label>
                <input
                    placeholder="auto from name if left blank"
                    value={item.id}
                    onChange={(e) =>
                        onChange({ ...item, id: e.target.value })
                    }
                />

                <label>Name</label>
                <input
                    value={item.name}
                    onChange={(e) =>
                        onChange({ ...item, name: e.target.value })
                    }
                />

                <label>Description</label>
                <textarea
                    rows={3}
                    value={item.description}
                    onChange={(e) =>
                        onChange({ ...item, description: e.target.value })
                    }
                />

                <label>Type</label>
                <select
                    value={item.type}
                    onChange={(e) =>
                        onChange({ ...item, type: e.target.value })
                    }
                >
                    {processTypes.map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>

                <label>Enabled</label>
                <input
                    type="checkbox"
                    checked={!!item.enabled}
                    onChange={(e) =>
                        onChange({ ...item, enabled: e.target.checked })
                    }
                />

                <label>Last Event</label>
                <input
                    placeholder="Optional; ISO datetime or note"
                    value={item.lastEvent}
                    onChange={(e) =>
                        onChange({ ...item, lastEvent: e.target.value })
                    }
                />
            </div>

            <div
                style={{
                    marginTop: 12,
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end"
                }}
            >
                <button type="button" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" onClick={onSave}>
                    Create
                </button>
            </div>
        </div>
    </div>
);

/* small helper for configure panel label column */
const LabelCell = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 600, paddingTop: 4 }}>
        {children}
    </div>
);

export default GradeExtractionProcessesPage;
